'use strict';

define('vb/private/stateManagement/switcherElement',[
  'vb/helpers/mixin',
  'vb/private/stateManagement/switcherRootMixin',
  'vb/private/stateManagement/navigationContext',
  'vb/private/history',
  'vb/private/stateManagement/routerUtils',
  'vb/private/utils',
  'vb/private/constants',
], (Mixin, SwitcherRootMixin, NavigationContext, History, RouterUtils, Utils, Constants) => {
  /**
   * Name of the variable used to store element info in the browser history
   *
   * @type {string}
   */
  const HISTORY_VARIABLE_NAME = 'switcherElt';

  /**
   * @typedef {import('vb/private/stateManagement/router').NavigateResult} NavigateResult
   * @typedef {import('vb/private/stateManagement/page').default} Page
   */

  /**
   * A class representing the info of a switcher element
   *
   */
  class SwitcherElement {
    constructor(data, switcher) {
      if (Utils.isObject(data)) {
        this.id = Utils.cleanString(data.id);
        this.name = Utils.cleanString(data.name);
        this.page = Utils.cleanString(data.page);
        this.flow = Utils.cleanString(data.flow);
        this.application = Utils.cleanString(data.application);
        this.params = data.params;
      }

      /**
       * Defined in switcherBridge addArray
       * @type {number|undefined}
       */
      this.index = undefined;

      /**
       * True while this element is exiting and must no longer accept navigation.
       * @type {boolean}
       */
      this.isRemoving = false;

      /**
       * The asynchronous cleanup currently removing this element, if any.
       * @type {Promise|undefined}
       */
      this.removalPromise = undefined;

      /**
       * The navigation that initialized this switcher element, if any.
       * @type {Promise|undefined}
       */
      this.initializationPromise = undefined;

      if (!this.id && (!this.flow || !this.application)) {
        throw new Error('Switcher element requires id and flow properties to be defined.');
      }

      // The type of page could be a Package page
      const pageType = switcher.parent.getParentFlow().constructor.PageClass;

      // Creates the root page of the switcher element
      this.root = new (Mixin(pageType).with(SwitcherRootMixin))(this, switcher);
    }

    /**
     * Retrieve the switcher element info persisted on the browser history
     *
     * @return {Object}  The switcher element info.
     */
    static getFromHistory() {
      return History.retrieveFromState(HISTORY_VARIABLE_NAME) || {};
    }

    /**
     * Initializes the given switcher element by navigating the root page
     *
     * @param  {boolean} [replace = false]
     * @return {Promise<NavigateResult>} A promise that resolve with the result of the navigate operation
     */
    initialize(replace = false) {
      const navParam = {
        operation: this.application ? Constants.NavigateOperation.APP_UI : Constants.NavigateOperation.FLOW,
        application: this.application,
        flow: this.flow,
        page: this.page,
        params: this.params,
        history: replace ? Constants.HistoryMode.REPLACE : undefined,
      };

      this.initializationPromise = this.root.navigateOperation(navParam,
        new NavigationContext(this.root, navParam));
      return this.initializationPromise;
    }

    /**
     * Wait until the displayed page has created its module view model and completed vbEnter.
     *
     * @return {Promise}
     */
    whenReady() {
      return Promise.resolve(this.initializationPromise)
        .then(() => this.root.rootRouter.getNavigationPromise())
        .then(() => {
          const moduleConfig = this.root.moduleConfig();
          return moduleConfig && moduleConfig.viewModel;
        })
        .then(() => {
          const currentPage = this.getCurrentPage();
          return currentPage && currentPage.enterPromise;
        });
    }

    /**
     * Retrieve the current page of the switcher element by traversing the router hierarchy
     * @return {Page}
     */
    getCurrentPage() {
      return RouterUtils.getCurrentPage(this.root.rootRouter);
    }

    storeInHistory() {
      if (this.root) {
        History.storeInState(HISTORY_VARIABLE_NAME, {
          flow: this.flow,
          page: this.page,
          id: this.id,
          name: this.name,
          application: this.application,
          routerName: this.root.rootRouter.name,
        });
      }
    }

    dispose() {
      if (this.root) {
        this.root.dispose();
        // Remove stored state from history so VbRootRouter.rootInstance does not return the deleted root router
        History.storeInState(HISTORY_VARIABLE_NAME, undefined);
        this.root = null;
      }
    }
  }

  return SwitcherElement;
});

